import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../../services/api';
import { normalizeImagePathForStorage } from '../../utils/format';
import { mapOfferForPos } from '../../utils/offers';

export const fetchOffers = createAsyncThunk(
  'offers/fetchOffers',
  async (activeOnly = false, { rejectWithValue }) => {
    try {
      const res = await apiService.getOffers(activeOnly);
      const rows = Array.isArray(res) ? res : Array.isArray(res?.data) ? res.data : [];
      return rows.map(mapOfferForPos);
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

export const addOffer = createAsyncThunk(
  'offers/addOffer',
  async (offer, { rejectWithValue }) => {
    try {
      const payload = {
        name: offer.name,
        selling_price: parseFloat(offer.selling_price) || 0,
        is_active: offer.is_active !== false,
        image_path: normalizeImagePathForStorage(offer.image_url || offer.image_path || null),
        items: (offer.items || []).map((it) => ({
          product_id: it.product_id,
          quantity: parseFloat(it.quantity) || 0,
        })),
      };
      if (offer.sku !== undefined) {
        payload.sku = offer.sku != null && String(offer.sku).trim() !== '' ? String(offer.sku).trim() : null;
      }
      const res = await apiService.createOffer(payload);
      return mapOfferForPos(res.data);
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

export const updateOffer = createAsyncThunk(
  'offers/updateOffer',
  async (offer, { rejectWithValue }) => {
    try {
      const payload = {};
      if (offer.name !== undefined) payload.name = offer.name;
      if (offer.sku !== undefined) {
        payload.sku = offer.sku != null && String(offer.sku).trim() !== '' ? String(offer.sku).trim() : null;
      }
      if (offer.selling_price !== undefined) payload.selling_price = parseFloat(offer.selling_price) || 0;
      if (offer.is_active !== undefined) payload.is_active = !!offer.is_active;
      if (offer.image_url !== undefined || offer.image_path !== undefined) {
        payload.image_path = normalizeImagePathForStorage(offer.image_url ?? offer.image_path ?? null);
      }
      if (offer.items !== undefined) {
        payload.items = offer.items.map((it) => ({
          product_id: it.product_id,
          quantity: parseFloat(it.quantity) || 0,
        }));
      }
      const res = await apiService.updateOffer(offer.id, payload);
      return mapOfferForPos(res.data);
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

export const deleteOffer = createAsyncThunk(
  'offers/deleteOffer',
  async (offerId, { rejectWithValue }) => {
    try {
      await apiService.deleteOffer(offerId);
      return { id: offerId };
    } catch (err) {
      return rejectWithValue(err.body?.error || err.message);
    }
  }
);

const offersSlice = createSlice({
  name: 'offers',
  initialState: {
    items: [],
    loading: false,
    error: null,
  },
  reducers: {
    clearOffersError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchOffers.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchOffers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchOffers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      .addCase(addOffer.fulfilled, (state, action) => {
        state.items.push(action.payload);
      })
      .addCase(updateOffer.fulfilled, (state, action) => {
        const i = state.items.findIndex((x) => x.id === action.payload.id);
        if (i !== -1) state.items[i] = action.payload;
      })
      .addCase(deleteOffer.fulfilled, (state, action) => {
        state.items = state.items.filter((x) => x.id !== action.payload.id);
      });
  },
});

export const { clearOffersError } = offersSlice.actions;
export default offersSlice.reducer;
